/**
 * Centralized Runtime Configuration
 * 
 * Single source of truth for API baseURL and runtime environment detection.
 * 
 * CRITICAL: Never fallback to localhost in production/staging builds.
 * If baseURL is missing in non-dev, throw an explicit error at app boot.
 */

/**
 * Get the current build mode (development, production, etc.)
 */
export function getBuildMode(): string {
  if (typeof import.meta !== 'undefined' && (import.meta as any).env?.MODE) {
    return (import.meta as any).env.MODE;
  }
  if (process.env.NODE_ENV) {
    return process.env.NODE_ENV;
  }
  // Default to development if we can't determine
  return 'development';
}

/**
 * Check if we're in development mode
 */
export function isDevelopment(): boolean {
  const mode = getBuildMode();
  return mode === 'development' || mode === 'dev';
}

/**
 * Get API base URL with strict validation
 * 
 * Priority:
 * 1. VITE_API_BASE_URL (or VITE_BACKEND_URL for backward compatibility)
 * 2. If not set in production: derive from window.location.origin (same-origin)
 * 3. If not set in development: use window.location.origin (localhost is OK)
 * 4. NEVER fallback to hardcoded localhost:5000 unless in development
 * 
 * @throws Error if baseURL is missing in non-dev mode
 */
export function getApiBaseUrl(): string {
  // Try VITE_API_BASE_URL first (new standard)
  let baseUrl = 
    (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_BASE_URL) ||
    process.env.VITE_API_BASE_URL ||
    // Backward compatibility with old env var names
    (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_BACKEND_URL) ||
    process.env.VITE_BACKEND_URL ||
    (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_URL) ||
    process.env.VITE_API_URL ||
    process.env.REACT_APP_API_URL ||
    process.env.REACT_APP_API_BASE ||
    '';

  // Normalize by removing trailing slashes
  baseUrl = baseUrl.replace(/\/+$/, '');

  const isDev = isDevelopment();

  // If baseURL is not set, derive from window.location.origin (same-origin)
  if (!baseUrl && typeof window !== 'undefined') {
    baseUrl = window.location.origin;
  }

  // CRITICAL: In production/staging, NEVER allow localhost
  if (!isDev && baseUrl) {
    if (baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1')) {
      const error = new Error(
        `[Runtime Config] Invalid baseURL in production: ${baseUrl}. ` +
        `Production builds must use a proper domain. Set VITE_API_BASE_URL environment variable.`
      );
      console.error(error.message);
      throw error;
    }
  }

  // If still no baseURL in production, throw error
  if (!isDev && !baseUrl) {
    const error = new Error(
      '[Runtime Config] API baseURL is required in production. ' +
      'Set VITE_API_BASE_URL environment variable or ensure window.location.origin is available.'
    );
    console.error(error.message);
    throw error;
  }

  // Ensure HTTPS if page is loaded over HTTPS
  if (typeof window !== 'undefined' && window.location.protocol === 'https:' && baseUrl) {
    baseUrl = baseUrl.replace(/^http:\/\//, 'https://');
  }

  // NOTE: We do NOT normalize subdomains (v1 vs www) here
  // If API is on v1.togthercrm.com and frontend is on www.togthercrm.com, 
  // CORS is already configured to allow cross-subdomain requests
  // Normalizing would break requests when API is intentionally on a different subdomain

  return baseUrl;
}

/**
 * Get tenant slug from current URL
 * Extracts the first path segment as the company/tenant slug
 */
export function getTenantSlug(): string {
  if (typeof window === 'undefined') return '';
  
  const parts = window.location.pathname.split('/').filter(Boolean);
  const slug = parts[0] || '';
  
  // Filter out reserved app prefixes
  if (['admin', 'manager', 'agent', 'app', 'superadmin'].includes(slug)) {
    return '';
  }
  
  return slug;
}

/**
 * Initialize and log runtime configuration (dev only)
 * Call this once at app startup
 */
export function logRuntimeConfig(): void {
  if (!isDevelopment()) return;
  
  const baseUrl = getApiBaseUrl();
  const mode = getBuildMode();
  const tenant = getTenantSlug();
  
}

/**
 * Assert that baseURL does not contain localhost in production
 * Use this for runtime validation
 */
export function assertProductionBaseUrl(): void {
  if (isDevelopment()) return;
  
  const baseUrl = getApiBaseUrl();
  if (baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1')) {
    throw new Error(
      `[Runtime Config] Production build detected localhost in baseURL: ${baseUrl}. ` +
      'This should never happen. Check environment variables and build configuration.'
    );
  }
}

